import React, { useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { StatusChip, fmtDate } from './AdminList';

/**
 * ADMIN → SELLER PROMOTIONS  (Feb 2026).
 * Admin looks up a seller by GST, then grants/extends promotional access.
 * The same underlying promotion record is updated across expiry / plan /
 * discount changes so the full audit history remains visible.
 */
export default function AdminSellerPromotions() {
  const [gst, setGst] = useState('');
  const [found, setFound] = useState(null);        // { seller, promotion }
  const [form, setForm] = useState({ plan: 'PRO', discount_percentage: 100,
                                     start_date: '', expiry_date: '', reason: '' });
  const [busy, setBusy] = useState(false);

  const search = async () => {
    if (!gst.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.get('/admin/seller-by-gst', { params: { gst: gst.trim() } });
      setFound(data);
      if (data.promotion) {
        setForm({
          plan: data.promotion.plan,
          discount_percentage: data.promotion.discount_percentage,
          start_date: (data.promotion.start_date || '').slice(0, 10),
          expiry_date: (data.promotion.expiry_date || '').slice(0, 10),
          reason: data.promotion.reason || '',
        });
      } else {
        const today = new Date().toISOString().slice(0, 10);
        const oneYear = new Date(Date.now() + 365 * 86400_000).toISOString().slice(0, 10);
        setForm({ plan: 'PRO', discount_percentage: 100, start_date: today, expiry_date: oneYear, reason: '' });
      }
    } catch (e) {
      setFound(null);
      toast.error(e?.response?.data?.detail || 'Seller not found');
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!found?.seller) return;
    setBusy(true);
    try {
      const body = {
        gst_number: found.seller.gst_number,
        plan: form.plan,
        discount_percentage: Number(form.discount_percentage),
        start_date: new Date(form.start_date).toISOString(),
        expiry_date: new Date(form.expiry_date).toISOString(),
        reason: form.reason || null,
      };
      const { data } = await api.post('/admin/promotions', body);
      toast.success('Promotion saved');
      setFound({ ...found, promotion: data.promotion });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!found?.promotion?.id) return;
    setBusy(true);
    try {
      await api.post(`/admin/promotions/${found.promotion.id}/disable`, {});
      toast.success('Promotion disabled');
      await search();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Disable failed');
    } finally { setBusy(false); }
  };

  const p = found?.promotion;
  const s = found?.seller;
  return (
    <div className="space-y-4" data-testid="admin-seller-promotions">
      <div className="bg-white border border-fab-primary-lighter rounded-xl p-4">
        <div className="text-[13px] font-semibold text-fab-text">Search seller by GST number</div>
        <div className="mt-2 flex gap-2 flex-wrap">
          <input value={gst} onChange={(e) => setGst(e.target.value.toUpperCase())}
                 placeholder="e.g. 24ABCDE1234F1Z5"
                 data-testid="promo-gst-input"
                 className="flex-1 min-w-[240px] border border-fab-primary-lighter rounded-lg px-3 py-1.5 text-[13px] outline-none" />
          <button onClick={search} disabled={busy || !gst.trim()}
                  data-testid="promo-search-btn"
                  className="btn-primary text-[12px] px-4 py-1.5 rounded-md disabled:opacity-60">
            {busy ? 'Searching…' : 'Search'}
          </button>
        </div>
      </div>

      {found && s && (
        <div className="bg-white border border-fab-primary-lighter rounded-xl p-4 space-y-2" data-testid="promo-seller-info">
          <div className="font-display font-bold text-fab-text text-[15px]">{s.business_name || s.name}</div>
          <div className="text-[12px] text-fab-text-secondary">GST · <code>{s.gst_number}</code> · Seller ID <code>{s.id?.slice(0, 8)}…</code></div>
          <div className="text-[12px] text-fab-text-secondary">Email {s.email} · Plan {s.plan_tier || 'FREE_TRIAL'} · {s.plan_status || 'ACTIVE'}</div>

          {p && (
            <div className="mt-3 rounded-lg bg-fab-primary/5 border border-fab-primary-lighter p-3" data-testid="promo-active-view">
              <div className="text-[11px] uppercase font-semibold text-fab-text-muted">Active promotion</div>
              <div className="mt-1 text-[14px] text-fab-text">
                <strong>{p.plan}</strong> · <strong>{p.discount_percentage}%</strong> OFF ·
                Start <strong>{p.start_date?.slice(0,10)}</strong> · Expiry <strong>{p.expiry_date?.slice(0,10)}</strong> ·
                <span className="ml-1"><StatusChip value={p.status} tone={{ ACTIVE:'green', SCHEDULED:'blue', EXPIRED:'red', DISABLED:'neutral' }[p.status] || 'neutral'} /></span>
              </div>
              <button onClick={disable} disabled={busy || p.status === 'DISABLED'}
                      data-testid="promo-disable-btn"
                      className="mt-2 text-[11px] text-fab-error underline disabled:opacity-40">Disable promotion</button>
            </div>
          )}

          <div className="mt-3 border-t border-fab-primary-lighter pt-3">
            <div className="text-[11px] uppercase font-semibold text-fab-text-muted mb-2">
              {p ? 'Update promotion (extend expiry, change plan, or change discount)' : 'Grant promotional access'}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[12px]">
              <label className="flex flex-col">Plan
                <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}
                        data-testid="promo-plan"
                        className="border border-fab-primary-lighter rounded px-2 py-1 mt-0.5">
                  {['STANDARD','PRO','ENTERPRISE'].map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </label>
              <label className="flex flex-col">Discount %
                <input type="number" min="1" max="100" value={form.discount_percentage}
                       onChange={(e) => setForm({ ...form, discount_percentage: e.target.value })}
                       data-testid="promo-discount"
                       className="border border-fab-primary-lighter rounded px-2 py-1 mt-0.5" />
              </label>
              <label className="flex flex-col">Start date
                <input type="date" value={form.start_date}
                       onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                       data-testid="promo-start" className="border border-fab-primary-lighter rounded px-2 py-1 mt-0.5" />
              </label>
              <label className="flex flex-col">Expiry date
                <input type="date" value={form.expiry_date}
                       onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                       data-testid="promo-expiry" className="border border-fab-primary-lighter rounded px-2 py-1 mt-0.5" />
              </label>
              <label className="md:col-span-4 flex flex-col">Reason / note (optional)
                <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                       data-testid="promo-reason"
                       className="border border-fab-primary-lighter rounded px-2 py-1 mt-0.5" />
              </label>
            </div>
            <button onClick={save} disabled={busy}
                    data-testid="promo-save-btn"
                    className="mt-3 btn-primary text-[12px] px-4 py-1.5 rounded-md disabled:opacity-60">
              {busy ? 'Saving…' : (p ? 'Save changes' : 'Grant promotional access')}
            </button>
          </div>

          {p && p.history && (
            <div className="mt-4 border-t border-fab-primary-lighter pt-3">
              <div className="text-[11px] uppercase font-semibold text-fab-text-muted mb-2">Promotion history</div>
              <ul className="text-[11.5px] space-y-1" data-testid="promo-history">
                {p.history.slice().reverse().map((h, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <StatusChip value={h.action.replace('PROMOTION_', '')} tone="blue" />
                    <span className="text-fab-text-secondary">{fmtDate(h.at)} · by {h.actor_email || h.actor_id}</span>
                    {h.previous && <code className="text-[10.5px] text-fab-text-muted ml-1">was {h.previous.plan} {h.previous.discount_percentage}% until {h.previous.expiry_date?.slice(0,10)}</code>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
